"use client";

import ArrowLeftIcon from "@/app/_components/Icons/ArrowLeftIcon";
import { alpha, Button, Card, CardActionArea, Icon, Stack, Typography } from "@mui/material";
import DOMPurify from "isomorphic-dompurify";
import { ReactNode } from "react";
import NextLink from "next/link";
import Image from "next/image";

type Props = {
  title?: string;
  arrowIcon?: ReactNode;
  svgIcon?: string | ReactNode;
  href: string;
  color?: string | null;
  image?: string | null;
};

export default function CarouselShowMoreCard(props: Props) {
  const circleSize = 50;
  const imageSize = 95;
  const {
    title = "مشاهده همه",
    href,
    svgIcon,
    image,
    arrowIcon = (
      <Icon sx={{ fontSize: "large" }}>
        <ArrowLeftIcon />
      </Icon>
    ),
  } = props;

  return (
    <Card elevation={0} sx={{ height: "100%" }}>
      <CardActionArea
        component={NextLink}
        href={href}
        sx={{
          position: "relative",
          textDecoration: "none",
          overflow: "hidden",
          backgroundColor: props.color ? alpha(props.color, 0.4) : "secondary.200",
          height: "100%",
          borderRadius: 4,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          alignItems: "center",
          justifyContent: "center",
          userSelect: "none",
        }}
      >
        {/* Priority: Image over Icon */}
        {image ? (
          <div
            style={{
              position: "relative",
              width: imageSize,
              height: imageSize,
              borderRadius: 50,
              background: props.color ? alpha(props.color, 1) : "secondary.50",
            }}
          >
            <Image
              src={image}
              alt={title}
              fill
              sizes={`(max-width: 768px) ${imageSize}px, ${imageSize}0px`}
              style={{ objectFit: "cover" }}
            />
          </div>
        ) : svgIcon ? (
          <Icon
            sx={{
              width: circleSize,
              height: circleSize,
              display: "flex",
              color: "secondary.main",
              alignItems: "center",
              justifyContent: "center",
              "& svg": { width: circleSize, height: circleSize },
            }}
            {...(typeof svgIcon === "string" ? { dangerouslySetInnerHTML: { __html: DOMPurify.sanitize(svgIcon) } } : {})}
          >
            {typeof svgIcon !== "string" ? svgIcon : null}
          </Icon>
        ) : null}

        <Button
          color="secondary"
          variant="contained"
          disableElevation
          sx={{
            borderRadius: 50,
            px: { xs: 2.5, sm: 3 },
            py: 1,
            color: props.color ? "primary.main" : "white.main",
            bgcolor: props.color ? alpha(props.color, 1) : "secondary.main",
            textTransform: "none",
            fontWeight: 600,
            letterSpacing: "-0.01em",
            whiteSpace: "nowrap",
            transition: "transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            "&:hover": {
              bgcolor: props.color ? alpha(props.color, 0.9) : undefined,
            },
            "&:active": {
              transform: "scale(0.97)",
            },
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle1" fontWeight="inherit">
              {title}
            </Typography>
            {arrowIcon}
          </Stack>
        </Button>
      </CardActionArea>
    </Card>
  );
}
